// 섹션 내용을 "헤더 아래 한 화면"에 들어가도록 필요한 만큼만 더 줄이는 배율.
//
// 가로는 화면 뿌리의 zoom(창폭/1920)이 맡는다. 그런데 세로는 그것만으로 안 맞는다 —
// 원본 디자인이 헤더를 포함한 한 화면을 전제로 그려지지 않았기 때문이다.
// 1920x1080(설계 1080) 에서 헤더 140 을 빼면 940 이 남는데, 원본 내용 높이는
//   미디어 940 · 거래소개 974 · 신뢰 1010 · 비전 1116
// 이라 세 섹션이 넘친다.
//
// 글자 크기나 간격을 따로 손대면 디자인이 망가진다. 그래서 넘치는 만큼만 통째로
// 줄인다 — 내부 비율은 그대로고 크기만 작아진다. 창이 높으면 1 이라 아무 일도 없다.
//
// transform 이 아니라 zoom 이다. transform 은 sticky/fixed 의 기준 상자를 빼앗는다
// (거래소개의 붙는 이미지, 디졸브 배경). zoom 은 그 크기로 다시 배치한다.
// 화면 뿌리의 zoom 과 곱해져서 걸린다.
import { onBeforeUnmount, onMounted, ref } from 'vue'

// 공통 상단이 늘 가리는 높이. 설계 기준 px 다.
//   시세 바 40 + 메뉴 60 + 시세표 띠 34 = 134 (맨 아래 테두리 1 은 안 센다)
// 이 값 하나만 고치면 된다 — 홈·IR·신뢰 섹션이 모두 여기서 가져다 쓴다.
export const HEADER_H = 134

// 홈은 2026-10-15 에 헤더를 배율 상자 밖으로 꺼냈다(지시: 헤더는 그대로).
// 그래서 헤더가 가리는 높이는 설계 px 가 아니라 **실제 px** 다 - 배율과
// 무관하게 늘 135 다. 홈 밖의 화면들은 헤더가 아직 배율 안이라 HEADER_H 를 쓴다.
export const HEADER_REAL = 135

// 기준가 줄(오늘의 거래 기준가)의 높이. REAL GOLD TRADE 안쪽 화면은 이 줄을 끄므로
// (`SiteHeader` 의 show-rates) 자리를 `HEADER_H - RATES_BAR_H` 로 잡는다.
export const RATES_BAR_H = 35

// 너무 작아지면 글을 못 읽는다. 이 아래로는 줄이지 않고 넘치게 둔다.
const FLOOR = 0.7

// 줄여야 할 때 바닥에 남기는 자리(설계 px).
//
// 딱 맞게 줄이면 내용 아랫변이 창 끝에 붙는다. 그러면 다 들어간 것인데도 잘린 것처럼
// 보이고, 스크롤이 몇 px 만 어긋나도 실제로 잘린다(2026-09-26 신뢰 섹션 제보).
// 넘치지 않는 섹션은 건드리지 않는다 - 남는 자리가 이미 제 여백이 된다.
const BREATH = 24

/**
 * @param {import('vue').Ref<HTMLElement|null>} el  내용 상자(이 요소에 zoom 이 걸린다)
 * @param {{ headroom?: number, breath?: number }} [opts]
 *   headroom  공용 상단이 가리는 높이. **실제 px** 다(헤더가 배율 밖이라서).
 *             기본은 HEADER_REAL 이다. 히어로는 헤더가 fixed 로
 *             그 위에 겹치므로(밀어내지 않는다) 0 을 준다 - 창 전체를 쓴다.
 *   breath    바닥에 남기는 자리. 기본은 BREATH 다. 히어로는 0 이다 - 카드 휠이 원래
 *             잘리도록 그려진 그림이라 남길 이유가 없고, 남기면 다음 섹션의 흰 바탕이
 *             첫 화면 아래에 띠로 비친다.
 * @returns {{ fit: import('vue').Ref<number>, measure: () => void }}
 */
export function useFitHeight(el, { headroom = HEADER_REAL, breath = BREATH } = {}) {
  const fit = ref(1)
  let observer = null

  const measure = () => {
    const node = el.value
    if (!node) return

    // 화면 뿌리가 내려 준 배율. 이 요소의 길이 단위는 설계 px 다.
    const z = parseFloat(getComputedStyle(node).getPropertyValue('--z')) || 1
    // headroom 은 실제 px 다. 창에서 헤더를 먼저 빼고, 남은 자리를 설계 px 로
    // 환산한다. 헤더가 배율 밖이라 배율을 먼저 나누면 안 된다.
    const available = (window.innerHeight - headroom) / z
    if (available <= 0) return

    // 잴 때는 배율을 풀어야 한다. 걸린 채로 재면 제 값을 되먹는다.
    // offsetHeight 다(scrollHeight 가 아니다). 연출 전에는 글 덩어리가 제 높이만큼
    // 아래로 내려가 있어서(translateY(100%)) scrollHeight 가 그만큼 부푼다 —
    // 거래소개가 858 대신 953 으로 재져 배율이 1.000 에서 0.936 으로 떨어졌었다.
    // offsetHeight 는 배치된 높이만 보므로 연출 상태와 무관하다.
    // 제목이 --fit 으로 제 크기를 되돌리고 있다. 그대로 재면 커진 제목이 높이에
    // 섞여 줄임이 더 걸리고, 그러면 제목이 또 커진다. 잴 때는 1 로 돌려 둔다.
    const had = node.style.zoom
    const hadFit = node.style.getPropertyValue('--fit')
    node.style.zoom = '1'
    node.style.setProperty('--fit', '1')
    const natural = node.offsetHeight
    node.style.zoom = had
    if (hadFit) node.style.setProperty('--fit', hadFit)
    else node.style.removeProperty('--fit')

    if (!natural) return
    const room = natural > available ? available - breath : available
    fit.value = Math.min(1, Math.max(FLOOR, room / natural))
  }

  onMounted(() => {
    // 쓰이지 않는 자리도 있다(히어로는 이 상자를 두지 않는다). 없으면 그냥 1 로 둔다.
    if (!el.value) return
    measure()
    // 글꼴이 늦게 오면 높이가 달라진다.
    document.fonts?.ready.then(measure)
    observer = new ResizeObserver(measure)
    observer.observe(el.value)
    // 폭이 그대로고 창 높이만 바뀌는 경우에는 ResizeObserver 가 울리지 않는다.
    window.addEventListener('resize', measure)
    // 그림이 늦게 오면 높이가 달라진다. 다 받은 뒤 한 번 더 잰다.
    window.addEventListener('load', measure)
  })

  onBeforeUnmount(() => {
    observer?.disconnect()
    observer = null
    window.removeEventListener('resize', measure)
    window.removeEventListener('load', measure)
  })

  return { fit, measure }
}
